type OutputKind = "objectURL" | "blob" | "dataURL";

export async function downscaleWithBitmap(
    url: string,
    minCssW: number,
    minCssH: number,
    opts: {
        cors?: "" | "anonymous" | "use-credentials";
        referrerPolicy?: ReferrerPolicy;
        dprAware?: boolean; // default true: multiplies mins by window.devicePixelRatio
        mime?: string; // default "image/jpeg"
        quality?: number; // default 0.9 (for jpeg/webp)
        output?: OutputKind; // default "objectURL"
    } = {}
): Promise<string | Blob> {
    const {
        cors = "anonymous",
        referrerPolicy,
        dprAware = true,
        mime = "image/png",
        quality = 1,
        output = "objectURL",
    } = opts;

    // ---- inner helpers (kept inside to satisfy "single function") ----
    const sizeByMinAR = (naturalW: number, naturalH: number, minW: number, minH: number) => {
        if (naturalW <= 0 || naturalH <= 0) return { w: 0, h: 0 };
        const mw = Math.max(0, Number(minW) || 0);
        const mh = Math.max(0, Number(minH) || 0);
        const s = Math.max(mw > 0 ? mw / naturalW : 0, mh > 0 ? mh / naturalH : 0, 0);
        if (s === 0) return { w: naturalW, h: naturalH };
        return { w: Math.ceil(naturalW * s), h: Math.ceil(naturalH * s) };
    };

    const loadViaFetchBitmap = async (): Promise<ImageBitmap> => {
        const resp = await fetch(url, {
            mode: cors ? "cors" : "no-cors",
            credentials: cors === "use-credentials" ? "include" : "omit",
            referrerPolicy,
            cache: "force-cache",
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status} ${resp.statusText}`);
        if (resp.type === "opaque") throw new Error("Opaque response (no CORS).");
        const blob = await resp.blob();
        if ("createImageBitmap" in window) {
            return await createImageBitmap(blob);
        }
        // Fallback: make an <img> from the blob
        const obj = URL.createObjectURL(blob);
        try {
            const img = await loadHTMLImage(obj);
            const bm = await htmlImageToBitmap(img);
            return bm;
        } finally {
            URL.revokeObjectURL(obj);
        }
    };

    const loadHTMLImage = (src: string): Promise<HTMLImageElement> =>
        new Promise((resolve, reject) => {
            const img = new Image();
            if (cors) img.crossOrigin = cors; // set BEFORE src
            if (referrerPolicy) img.referrerPolicy = referrerPolicy;
            img.decoding = "async";
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error("Image load error"));
            img.src = src;
        });

    const htmlImageToBitmap = async (img: HTMLImageElement): Promise<ImageBitmap> => {
        if ("createImageBitmap" in window) {
            try {
                return await createImageBitmap(img);
            } catch {
                /* fall through */
            }
        }
        // final fallback: draw to canvas then create bitmap (may fail if tainted)
        const c = document.createElement("canvas");
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        const ctx = c.getContext("2d");
        if (!ctx) throw new Error("2D context unavailable");
        ctx.drawImage(img, 0, 0);
        if ("createImageBitmap" in window) {
            try {
                return await createImageBitmap(c);
            } catch {
                /* fall through */
            }
        }
        throw new Error("Bitmap creation failed (likely CORS-tainted).");
    };

    const toBlobAsync = (canvas: HTMLCanvasElement) =>
        new Promise<Blob>((resolve, reject) =>
            canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), mime, quality)
        );

    const finalize = async (blob: Blob): Promise<string | Blob> => {
        if (output === "blob") return blob;
        if (output === "dataURL") {
            const r = new FileReader();
            return await new Promise<string>((res, rej) => {
                r.onload = () => res(r.result as string);
                r.onerror = () => rej(r.error ?? new Error("FileReader error"));
                r.readAsDataURL(blob);
            });
        }
        return URL.createObjectURL(blob); // objectURL
    };

    // ---- load image robustly ----
    let bitmap: ImageBitmap;
    try {
        bitmap = await loadViaFetchBitmap();
    } catch {
        // Fallback path: direct <img src=url> (sometimes succeeds where fetch fails)
        const img = await loadHTMLImage(url);
        bitmap = await htmlImageToBitmap(img);
    }

    // ---- compute target size (preserve AR; meet mins × DPR if enabled) ----
    const dpr = dprAware ? Math.max(1, window.devicePixelRatio || 1) : 1;
    const minW = Math.round(minCssW * dpr);
    const minH = Math.round(minCssH * dpr);
    const { w, h } = sizeByMinAR(bitmap.width, bitmap.height, minW, minH);
    if (w === 0 || h === 0) throw new Error("Invalid dimensions after sizeByMinAR.");

    // ---- draw + export ----
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d");
    if (!ctx) throw new Error("2D context unavailable");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    const blob = await toBlobAsync(c);
    return await finalize(blob);
}
